import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import FAQ from "@/components/common/FAQ";
import { useMeta } from "@/hooks/useMeta";

export default function FaqArticlePage({ config }) {
  useMeta(config.title, config.description);

  useEffect(() => {
    // Inject FAQ schema
    const injectSchema = (schema, id) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = id;
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    };

    if (config.faqItems?.length) {
      injectSchema(
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: config.faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        },
        "faq-schema"
      );
    }

    injectSchema(
      {
        "@context": "https://schema.org",
        "@type": ["Article", "HowTo"],
        headline: config.h1,
        description: config.description,
        publisher: { "@type": "Organization", name: "BRIMO Immobilien NRW" },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: "/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Verkaufen",
              item: "/immobilie-verkaufen-kreis-mettmann",
            },
            { "@type": "ListItem", position: 3, name: config.breadcrumb },
          ],
        },
      },
      "article-schema"
    );

    return () => {
      ["faq-schema", "article-schema"].forEach((id) => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, [config]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [config.slug]);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Article Header */}
      <section className="pt-28 pb-12 bg-[#0d0d0d]" data-testid="article-header">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <nav
            className="flex items-center gap-1.5 text-gray-500 text-xs mb-6"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-[#E61D38] transition-colors">
              Start
            </Link>
            <ChevronRight size={10} />
            <Link
              to="/immobilie-verkaufen-kreis-mettmann"
              className="hover:text-[#E61D38] transition-colors"
            >
              Verkaufen
            </Link>
            <ChevronRight size={10} />
            <span className="text-gray-400">{config.breadcrumb}</span>
          </nav>
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase tracking-tight leading-tight max-w-4xl"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {config.h1}
          </h1>
          <p className="text-gray-400 mt-5 max-w-2xl text-sm leading-relaxed">{config.intro}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20 bg-[#1a1a1a]" data-testid="article-content">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Article body */}
            <div className="lg:col-span-2">
              <div className="space-y-12">
                {config.sections?.map((section, i) => (
                  <div key={i} data-testid={`article-section-${i}`}>
                    <h2
                      className="text-xl md:text-2xl font-black text-white uppercase tracking-tight mb-3"
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      {section.heading}
                    </h2>
                    <div className="h-0.5 w-8 bg-[#E61D38] mb-4" />
                    <p className="text-gray-400 text-sm leading-relaxed">{section.content}</p>
                    {section.bullets && (
                      <ul className="mt-4 space-y-2.5">
                        {section.bullets.map((b, j) => (
                          <li key={j} className="flex items-start gap-3 text-gray-300 text-sm">
                            <span className="text-[#E61D38] font-bold mt-0.5 flex-shrink-0">—</span>
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              {/* Inline FAQ */}
              {config.faqItems?.length > 0 && (
                <div className="mt-16">
                  <FAQ items={config.faqItems} title="Häufige Fragen" />
                </div>
              )}

              {/* CTA Banner */}
              <div className="mt-10 bg-[#E61D38] p-8" data-testid="article-cta-banner">
                <h3
                  className="text-white font-black text-lg uppercase tracking-wide mb-2"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Jetzt kostenlos beraten lassen
                </h3>
                <p className="text-white/80 text-sm mb-6">
                  BRIMO Immobilien NRW – persönlicher Ansprechpartner, lokale Expertise.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/immobilie-verkaufen-kreis-mettmann"
                    className="flex items-center gap-2 bg-white text-[#E61D38] px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gray-100 transition-colors"
                    data-testid="article-cta-sell"
                  >
                    Immobilie verkaufen
                    <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/marktwertermittlung-kreis-mettmann"
                    className="flex items-center gap-2 border-2 border-white text-white px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-white/10 transition-colors"
                    data-testid="article-cta-value"
                  >
                    Marktwert ermitteln
                  </Link>
                </div>
              </div>

              {/* Related Links */}
              {config.relatedLinks?.length > 0 && (
                <div className="mt-10 bg-[#262626] border border-white/5 p-6">
                  <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-5">
                    Weitere hilfreiche Artikel
                  </h3>
                  <ul className="space-y-2">
                    {config.relatedLinks.map((link, i) => (
                      <li key={i}>
                        <Link
                          to={link.href}
                          className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#E61D38] transition-colors"
                          data-testid={`related-link-${i}`}
                        >
                          <ArrowRight size={12} className="text-[#E61D38] flex-shrink-0" />
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div>
              <div className="sticky top-24">
                <LeadForm
                  formType="seller"
                  title="Kostenlose Beratung"
                  ctaText="Beratung anfragen"
                  showPropertyType={true}
                  showDistrict={true}
                  pageSource={`/${config.slug}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
